/**
 * Yelp Daily Call-Budget Circuit-Breaker (Story 23.5 AC #7)
 *
 * Yelp Fusion's primary rate-limit is per-day (Starter 300/day, Plus
 * 500/day, Enterprise 5000+/day) per docs.developer.yelp.com/docs/
 * fusion-rate-limiting verified 2026-05-18. The per-tenant counter at
 * `yelp:budget:{tenantId}:{YYYY-MM-DD-UTC}` increments on every successful
 * Yelp API call AND expires at midnight UTC. The hard ceiling is **250
 * calls/day** (83% of Starter, leaving headroom for the cron's 96
 * health-checks/day plus 24-48 fetchMetrics/day).
 *
 * **Separate keyspace for health-checks** (AC #22): the cron's
 * validateConnection counts to `yelp:budget:healthcheck:{tenantId}:{date}`
 * (no ceiling) — separate from the sync-call counter so cost-allocation
 * observability isn't muddled.
 *
 * Round 2 Pre-mortem C: without this circuit-breaker, a runaway cron could
 * exhaust the customer's 300-call daily limit and break all syncs. Round 2
 * Pre-mortem D: customer with multiple Yelp integrations on a shared
 * Starter key WILL still exhaust budget — recommend Plus/Enterprise tier.
 *
 * KV semantics — Workers KV does NOT support atomic INCR. We use
 * `get` + `put` (small race window OK since 250 is a soft ceiling, not a
 * hard cliff — Yelp's real ceiling is 300 so 50 calls of slop is safe).
 */

/**
 * Minimal Workers KV binding shape. Matches the existing pattern used in
 * `cache.ts` and Moz's `row-budget.ts`.
 *
 * The `expiration` field (absolute epoch-seconds) is preferred over
 * `expirationTtl` (relative seconds) for the daily-budget counter — see
 * Round 1 review fix below, `incrementYelpDailyBudget`.
 */
export interface YelpBudgetKv {
  get(key: string, options?: { type?: "text" | "json" }): Promise<string | null>;
  put(
    key: string,
    value: string,
    options?: { expirationTtl?: number; expiration?: number },
  ): Promise<void>;
}

/** Hard ceiling on the per-tenant daily call counter. 83% of Starter 300/day. */
export const YELP_DAILY_BUDGET_CEILING = 250;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Compute the UTC seconds-until-next-midnight from a Date (or now).
 * Used for KV `expirationTtl`. Capped at 86400 to defend against
 * pathological clock drift.
 */
export function secondsUntilUtcMidnight(now?: Date): number {
  const d = now ?? new Date();
  const next = new Date(d.getTime());
  next.setUTCHours(24, 0, 0, 0);
  const diffSec = Math.floor((next.getTime() - d.getTime()) / 1000);
  return Math.max(60, Math.min(86_400, diffSec));
}

/**
 * Compute the absolute UTC epoch-second at next midnight from a Date.
 * Used for KV's `expiration` field (NOT `expirationTtl`) so successive puts
 * to the same key do NOT slide the expiry window forward — every put points
 * at the SAME midnight UTC boundary regardless of when it fires.
 *
 * Round 1 review fix (Blind Hunter): the prior `expirationTtl` approach
 * computed a relative-from-now duration on every increment, causing the
 * counter key to persist longer than its calendar day under continuous load.
 */
export function nextUtcMidnightEpochSec(now?: Date): number {
  const d = now ?? new Date();
  const next = new Date(d.getTime());
  next.setUTCHours(24, 0, 0, 0);
  return Math.floor(next.getTime() / 1000);
}

/**
 * Safely parse a KV-stored counter to a non-negative integer.
 *
 * Round 1 review fix (Blind Hunter Finding): `Number(raw) || 0` silently
 * passes through negative or non-finite values that would bypass the
 * ceiling check (`-9999 < 250` is true). Floor + clamp to [0, +inf).
 */
function parseUsedCounter(raw: string | null): number {
  if (!raw) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

/** Build the UTC YYYY-MM-DD string. */
export function utcDateKey(now?: Date): string {
  return (now ?? new Date()).toISOString().slice(0, 10);
}

function buildSyncBudgetKey(tenantId: string, date: string): string {
  return `yelp:budget:${tenantId}:${date}`;
}

function buildHealthcheckBudgetKey(tenantId: string, date: string): string {
  return `yelp:budget:healthcheck:${tenantId}:${date}`;
}

// ============================================================================
// Public API — sync-call budget
// ============================================================================

export interface CheckYelpDailyBudgetResult {
  readonly available: boolean;
  readonly used: number;
  readonly ceiling: number;
}

/**
 * Read the current sync-call budget. Used BEFORE every Yelp API call in
 * `fetchMetrics` — the call is short-circuited when `available === false`.
 */
export async function checkYelpDailyBudget(
  kv: YelpBudgetKv,
  tenantId: string,
  now?: Date,
): Promise<CheckYelpDailyBudgetResult> {
  if (!tenantId) throw new TypeError("checkYelpDailyBudget requires tenantId.");
  const date = utcDateKey(now);
  // Round 3 review fix (Blind Hunter CRITICAL): Yelp's hard server ceiling is
  // 300/day on Starter. The cron's 96 health-checks/day per integration
  // PLUS 250/day sync ceiling = 346/day total, overrunning the real
  // server limit by 46 calls. Make the ceiling check count BOTH keyspaces
  // so the circuit-breaker enforces the actual Yelp limit.
  const [syncRaw, healthRaw] = await Promise.all([
    kv.get(buildSyncBudgetKey(tenantId, date)),
    kv.get(buildHealthcheckBudgetKey(tenantId, date)),
  ]);
  const syncUsed = parseUsedCounter(syncRaw);
  const healthUsed = parseUsedCounter(healthRaw);
  const used = syncUsed + healthUsed;
  return {
    available: used < YELP_DAILY_BUDGET_CEILING,
    used,
    ceiling: YELP_DAILY_BUDGET_CEILING,
  };
}

/**
 * Increment the sync-call counter by 1. Returns the new counter value.
 * Tolerates the small race window inherent in non-atomic KV INCR — the
 * 250 ceiling is a soft cliff with 50 calls of slop before Yelp's hard
 * 300/day cliff.
 */
export async function incrementYelpDailyBudget(
  kv: YelpBudgetKv,
  tenantId: string,
  now?: Date,
): Promise<number> {
  if (!tenantId) throw new TypeError("incrementYelpDailyBudget requires tenantId.");
  const date = utcDateKey(now);
  const key = buildSyncBudgetKey(tenantId, date);
  const raw = await kv.get(key);
  const next = parseUsedCounter(raw) + 1;
  // Use ABSOLUTE `expiration` (epoch-second) so successive puts to the same
  // key all point at the same midnight UTC boundary — the entry expires at
  // calendar-day rollover regardless of write time (Round 1 Blind Hunter fix).
  await kv.put(key, String(next), {
    expiration: nextUtcMidnightEpochSec(now),
  });
  return next;
}

// ============================================================================
// Public API — healthcheck budget (separate keyspace)
// ============================================================================

export async function checkYelpHealthcheckBudget(
  kv: YelpBudgetKv,
  tenantId: string,
  now?: Date,
): Promise<{ used: number }> {
  if (!tenantId)
    throw new TypeError("checkYelpHealthcheckBudget requires tenantId.");
  const key = buildHealthcheckBudgetKey(tenantId, utcDateKey(now));
  const raw = await kv.get(key);
  const used = parseUsedCounter(raw);
  return { used };
}

export async function incrementYelpHealthcheckBudget(
  kv: YelpBudgetKv,
  tenantId: string,
  now?: Date,
): Promise<number> {
  if (!tenantId)
    throw new TypeError("incrementYelpHealthcheckBudget requires tenantId.");
  const date = utcDateKey(now);
  const key = buildHealthcheckBudgetKey(tenantId, date);
  const raw = await kv.get(key);
  const next = parseUsedCounter(raw) + 1;
  await kv.put(key, String(next), {
    expiration: nextUtcMidnightEpochSec(now),
  });
  return next;
}
